import React, { createContext, useContext, useState, useCallback, ReactNode, RefObject } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';
type ToastPlacement = 'top' | 'bottom' | 'left' | 'right';

interface ToastPositioning {
  targetRef?: RefObject<HTMLElement>;
  placement?: ToastPlacement;
  offset?: number;
  showOverlay?: boolean;
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  positioning?: ToastPositioning;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, options?: {
    type?: ToastType;
    targetRef?: RefObject<HTMLElement>;
    placement?: ToastPlacement;
    offset?: number;
    showOverlay?: boolean;
  }) => number;
  removeToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, options: {
    type?: ToastType;
    targetRef?: RefObject<HTMLElement>;
    placement?: ToastPlacement;
    offset?: number;
    showOverlay?: boolean;
  } = {}) => {
    const {
      type = 'info',
      targetRef,
      placement = 'top',
      offset = 8,
      showOverlay = false
    } = options;

    const id = Date.now();
    const newToast = {
      id,
      message,
      type,
      positioning: targetRef ? {
        targetRef,
        placement,
        offset,
        showOverlay
      } : undefined
    };

    setToasts(prevToasts => [...prevToasts, newToast]);
    return id;
  }, []);
  
  const removeToast = useCallback((id: number) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};